/**
 * MUI Modal Component
 * 
 * This component is the Material UI equivalent of the original Modal component.
 * It renders a draggable modal dialog for the Room Designer application.
 */

import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  ThemeProvider,
  createTheme,
  Popper,
  Paper,
  Fade as MuiFade
} from '@mui/material';
import DialogTitle from '../atoms/DialogTitle';
import RdButton from '../atoms/RdButton';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import Fade from '../services/Fade';
import { MuiError as Error, MuiInfo as Info, MuiHyperlink as Hyperlink } from '../atoms/PlainText';
import { ComponentsIndex } from '../../globalConstants/ComponentIndex';
import ReducersIndex from '../../reduxModules/ducks';
import { useModalThemes } from '../../globalConstants/Themes';
import ToolContext, { ToolContextType } from '../services/ToolContext';
import type { RootState } from 'types/redux/state';

// Import the extracted Line component
import Line from './Line';

// Define interfaces for TypeScript
interface ModalFooterButtonProps {
  button: {
    label?: string;
    target?: string;
    color?: string;
    style?: 'contained' | 'outlined';
    condition?: any;
    content?: {
      [key: string]: {
        label?: string;
        target?: string;
        dispatcher?: {
          name: string;
          value: any;
        };
      }
    };
    dispatcher?: {
      name: string;
      value: any;
    };
  };
  index: number;
  hideNext: boolean;
  modalReopen?: string;
  closeModal: (evt: React.MouseEvent, modal?: string | null, dispatcher?: { name: string; value: any } | null) => void;
  center: boolean;
  modalName?: string;
}

interface ModalFooterButtonsProps {
  buttons: ModalFooterButtonProps['button'][];
  hideNext: boolean;
  modalReopen?: string;
  closeModal: ModalFooterButtonProps['closeModal'];
  modalName?: string;
}

interface ModalProps {
  modalName: string;
  top?: number;
}

/**
 * Modal Footer Button component
 * 
 * @param props - Component props
 * @returns The Modal Footer Button component
 */
const ModalFooterButton: React.FC<ModalFooterButtonProps> = ({
  button, index, hideNext, modalReopen, closeModal, center, modalName
}) => {
  const key = useSelector((state: any) => {
    // Since we need to implement proper type for this function in the future
    return state.container.getConditionResult ? 
      state.container.getConditionResult(state, {...button?.condition}) : 
      button?.condition?.key || 'default';
  });
  
  let buttonProps = { ...button };
  
  if (button.condition) {
    buttonProps = {
      ...buttonProps,
      label: button.content?.[key]?.label,
      target: button.content?.[key]?.target,
      dispatcher: button.content?.[key]?.dispatcher
    };
  }
  
  // Check if this is the requestQuote modal
  if (modalName === 'requestQuote') {
    const isSubmit = buttonProps.label?.toLowerCase() === 'submit';

    return (
      <RdButton
        key={`btn-${index}`}
        buttonType={isSubmit ? 'primary' : undefined}
        onClick={buttonProps.label && !hideNext
          ? (e: React.MouseEvent) => closeModal(e, (modalReopen ?? buttonProps.target), buttonProps.dispatcher)
          : () => null}
        disabled={hideNext}
        sx={{
          flex: '1',
          maxWidth: 'calc(50% - 8px)',
          marginBottom: '0px !important',
        }}
      >
        {buttonProps.label}
      </RdButton>
    );
  }

  // Check if this is a modern styled button
  const isModernStyle = buttonProps.style === 'contained' || buttonProps.style === 'outlined';

  if (isModernStyle) {
    const isOutlined = buttonProps.style === 'outlined';
    const isPrimary = buttonProps.label?.toLowerCase().includes('submit') ||
                     buttonProps.label?.toLowerCase().includes('ok') ||
                     buttonProps.label?.toLowerCase().includes('request');

    return (
      <Box
        component="button"
        key={`btn-${index}`}
        onClick={buttonProps.label && !hideNext
          ? (e: React.MouseEvent) => closeModal(e, (modalReopen ?? buttonProps.target), buttonProps.dispatcher)
          : () => null}
        disabled={hideNext}
        sx={{
          backgroundColor: isOutlined ? 'transparent' : '#d9d9d9',
          color: '#333333',
          border: isOutlined ? '1px solid #d9d9d9' : 'none',
          borderRadius: '8px',
          padding: '12px 32px',
          fontSize: '16px',
          fontWeight: '400',
          fontFamily: '"Inter", "Roboto", sans-serif',
          cursor: hideNext ? 'not-allowed' : 'pointer',
          flex: '1',
          maxWidth: 'calc(50% - 8px)',
          height: '48px',
          opacity: hideNext ? 0.5 : 1,
          transition: 'all 0.2s ease',
          '&:hover': !hideNext && {
            backgroundColor: isOutlined ? 'rgba(0,0,0,0.02)' : '#c9c9c9',
            borderColor: isOutlined ? '#c9c9c9' : undefined
          }
        }}
      >
        {buttonProps.label}
      </Box>
    );
  }
  
  // Fallback to original button component for non-modern styles
  const Element = ComponentsIndex['button'];
  const theme = { 
    background: 'transparent', 
    color: hideNext ? 'var(--app-custom-gray)' : buttonProps.color, 
    width: center ? '18%' : '' 
  };
  
  const onClick = buttonProps.label && !hideNext 
    ? (e: React.MouseEvent) => closeModal(e, (modalReopen ?? buttonProps.target), buttonProps.dispatcher) 
    : () => null;
  
  return (
    <Element 
      key={`btn-${index}`} 
      className={`btn-${index}`} 
      theme={theme} 
      label={buttonProps.label} 
      onClick={onClick} 
    />
  );
};

/**
 * Modal Footer Buttons component
 * 
 * @param props - Component props
 * @returns The Modal Footer Buttons component
 */
const ModalFooterButtons: React.FC<ModalFooterButtonsProps> = ({
  buttons, hideNext, modalReopen, closeModal, modalName
}) => {
  const center = buttons.length <= 1 || buttons.some(btn => btn.label === 'CANCEL');

  return (
    <>
      {buttons.map((button, index) => (
        <ModalFooterButton
          key={`footer-btn-${index}`}
          button={button}
          index={index}
          hideNext={hideNext}
          modalReopen={modalReopen}
          closeModal={closeModal}
          center={center}
          modalName={modalName}
        />
      ))}
    </>
  );
};

/**
 * Paper component for Draggable
 */
const PaperComponent = (props: any) => {
  const nodeRef = React.useRef(null);

  return (
    <Draggable
      handle=".modalHeader"
      bounds="parent"
      nodeRef={nodeRef}
    >
      <Box ref={nodeRef} {...props} />
    </Draggable>
  );
};

/**
 * MUI Modal component
 * 
 * @param props - Component props
 * @returns The MUI Modal component
 */
const MuiModal: React.FC<ModalProps> = ({ modalName, top = 0 }) => {
  const toolContext = useContext(ToolContext) as ToolContextType;
  const navigate = useNavigate();
  
  // Use default empty object if context or modals are undefined
  // First try to get from tool context, then fall back to checking if it's a global dialog
  const modalData = toolContext?.modals?.[modalName] || {};
  const { dialog, ...initialModalContent } = modalData;
  
  // Debug logging for requestQuote modal
  if (modalName === 'requestQuote') {
    console.log('RequestQuote modal debug:', {
      modalName,
      hasToolContext: !!toolContext,
      hasModals: !!toolContext?.modals,
      modalKeys: toolContext?.modals ? Object.keys(toolContext.modals) : [],
      modalData,
      hasRequestQuote: !!toolContext?.modals?.requestQuote
    });
  }
  
  // Merge dialog content with modal content if dialog exists
  let modalContent = initialModalContent;
  if (dialog && toolContext?.modals?.[dialog]) {
    modalContent = { ...toolContext.modals[dialog], ...initialModalContent }; 
  }
  
  // If modal content is still empty and we have the modal name, it might be rendering before context is ready
  // Show a default structure to prevent "Oops" message
  if (!modalContent.header && modalName === 'requestQuote') {
    modalContent = {
      header: 'Request a Quote',
      body: [
        {
          element: 'plainText',
          type: 'p',
          content: "Loading form...",
          extend: true
        }
      ],
      footer: []
    };
  }

  // Tutorial specific state
  const tutorialStepFromRedux = useSelector((state: RootState) => state.ui.tutorialStep);
  const [currentStep, setCurrentStep] = useState(tutorialStepFromRedux || 0);
  const tutorialConfig = useSelector((state: RootState) => state.ui.tutorialConfig);
  const isTutorial = modalName === 'tutorial';
  const [targetElementTop, setTargetElementTop] = useState<number | null>(null);
  const spotlightTarget = useSelector((state: RootState) => state.ui.spotlightTarget);
  
  // Check if WalkthroughGuide is active (it sets spotlightTarget to nav sections)
  const isWalkthroughGuideActive = spotlightTarget && 
    ['floor_plan', 'ceiling', 'furnishing', 'acoustics', 'microphones', 'speakers', 'camera', 'equipment_list'].includes(spotlightTarget);
    

  const dispatch = useDispatch();
  
  // Sync with Redux tutorial step
  useEffect(() => {
    if (isTutorial && tutorialStepFromRedux !== undefined && tutorialStepFromRedux !== currentStep) {
      setCurrentStep(tutorialStepFromRedux);
    }
  }, [tutorialStepFromRedux, isTutorial]);
  
  // Update Redux state when tutorial step changes
  useEffect(() => {
    // Don't interfere if WalkthroughGuide is active
    if (isWalkthroughGuideActive) {
      return;
    }
    
    if (isTutorial && tutorialConfig) {
      dispatch(ReducersIndex.uiReducers.setTutorialStep(currentStep) as any);
      const step = tutorialConfig[currentStep];
      
      // Set spotlight target if defined in current step
      if (step?.target) {
        
        // Add a small delay to ensure the target element exists
        setTimeout(() => {
          const targetElement = document.getElementById(step.target);
          if (targetElement) {
            // Calculate the position of the target element
            const rect = targetElement.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const elementTop = rect.top + scrollTop;
            setTargetElementTop(elementTop);
          } else {
            // Default position if no target element
            setTargetElementTop(null);
          }
          dispatch(ReducersIndex.uiReducers.setSpotlightTarget(step.target) as any);
        }, 100);
      } else {
        setTargetElementTop(null);
      }
      
      // Activate menu if defined in current step
      if (step?.menu) {
        dispatch(ReducersIndex.uiReducers.setCurrentView(step.menu) as any);
      }
    }
  }, [currentStep, isTutorial, tutorialConfig, dispatch, isWalkthroughGuideActive]);
  
  // We need to properly type these selectors in the future
  const currentView = useSelector((state: any) => state.ui.getCurrentView ? state.ui.getCurrentView(state) : null);
  const exitToRoom = useSelector((state: RootState) => state.ui.exitToRoom);
  const inError = useSelector((state: any) => state.ui.getErrorState ? state.ui.getErrorState(state) : false);
  const firstTab = useSelector((state: RootState) => state.ui.tabs[0]?.id);
  const activeTab = useSelector((state: RootState) => state.container.activeTab);
  const hasCamera = useSelector((state: RootState) => 
    state.container.areas[activeTab]?.cameras?.hasCamera);
  const hasVideoBar = useSelector((state: RootState) => 
    state.container.areas[activeTab]?.microphones?.model === 'videobar');
  const hasSpotlight = useSelector((state: RootState) => state.ui.spotlightTarget);
  const modalReopen = useSelector((state: RootState) => state.ui.modalReopen);
  const hideNext = useSelector((state: RootState) => state.ui.isFirstTimeUser) && modalName === 'tutorial';

  const show = useSelector((state: RootState) => {
    const modal = state.ui.modals.find(modal => modal.modalName === modalName);
    return modal ? modal.modalOpen : false;
  });

  // Get theme-aware modal themes
  const modalThemes = useModalThemes();

  // Ensure we have a valid theme for Material UI
  const defaultTheme = modalThemes['default'] || {}; // Default theme as fallback
  const themeKey = modalContent?.theme || 'default';
  const useTheme = modalThemes[themeKey] || defaultTheme;

  // Create a proper MUI theme based on modal theme
  const isDarkMode = useTheme.background === '#1e1e1e' || useTheme.background === '#2d2f38';
  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      background: {
        paper: useTheme.background || (isDarkMode ? '#1e1e1e' : '#fff'),
        default: useTheme.background || (isDarkMode ? '#1e1e1e' : '#fff'),
      },
      text: {
        primary: useTheme.color || (isDarkMode ? '#e6e7e8' : '#333'),
        secondary: isDarkMode ? '#b0b3b8' : '#666',
      },
      primary: {
        main: isDarkMode ? '#1976d2' : '#1976d2',
        contrastText: '#fff',
      },
      divider: isDarkMode ? '#4a4d50' : 'rgba(0, 0, 0, 0.12)',
      action: {
        hover: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        disabled: isDarkMode ? '#666' : '#999',
        disabledBackground: isDarkMode ? '#2d2f38' : '#f5f5f5',
      },
    },
    ...useTheme
  });

  // Handle tutorial navigation
  const handleTutorialNext = () => {
    // Special handling for welcome screen - navigate to conference room designer
    if (tutorialConfig && tutorialConfig[currentStep]?.isWelcomeScreen) {
      // Save tutorial state to continue in conference room designer
      sessionStorage.setItem('tutorialStep', '1'); // Next step after welcome
      sessionStorage.setItem('continueGuide', 'true');
      
      // Close modal and navigate
      dispatch(ReducersIndex.uiReducers.closeModal(modalName));
      navigate('/conference_room_designer');
    } else if (tutorialConfig && currentStep < tutorialConfig.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleTutorialPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleTutorialSkip = () => {
    setCurrentStep(0);
    dispatch(ReducersIndex.uiReducers.setSpotlightTarget(null) as any);
    dispatch(ReducersIndex.uiReducers.closeModal(modalName) as any);
  };

  /**
   * Close the modal
   * 
   * @param evt - The event
   * @param modal - The modal to open after closing
   * @param dispatcher - The dispatcher to call
   */
  const close = (
    evt: React.MouseEvent, 
    modal: string | null = null, 
    dispatcher: { name: string; value: any } | null = null
  ): void => {
    const shouldClose = modalName !== 'requestQuote' || 
      (evt.target as HTMLElement).innerText === 'Cancel' ||
      (evt.target as HTMLElement).innerText === 'CANCEL';
    
    // Call dispatcher first if it exists (e.g., closeWarning for warning modals)
    if (dispatcher) {
      if (dispatcher.name in ReducersIndex.container) {
        dispatch(ReducersIndex.container[dispatcher.name](dispatcher.value) as any);
      }
      if (dispatcher.name in ReducersIndex.uiReducers) {
        // Handle dispatchers with or without values
        if (dispatcher.value !== undefined) {
          dispatch(ReducersIndex.uiReducers[dispatcher.name](dispatcher.value) as any);
        } else {
          dispatch(ReducersIndex.uiReducers[dispatcher.name]() as any);
        }
      }
    }
    
    if (shouldClose) {
      dispatch(ReducersIndex.uiReducers.closeModal(modalName));
    }
    
    // Open the next modal if specified
    if (modal && toolContext?.modals && modal in toolContext.modals) {
      dispatch(ReducersIndex.uiReducers.openModal(modal));
    }
    
    // Handle post-close logic when there's no next modal and not in error state
    // BUT NOT when we're closing a warning modal (which is just dismissing an overlay)
    if (shouldClose && !modal && !inError && modalName !== 'warning') {
      const landingMenu = toolContext?.constants?.tool?.landingMenu || 'default';
      dispatch(ReducersIndex.uiReducers.setCurrentView(
        exitToRoom ? currentView : landingMenu
      ));
      if (!exitToRoom) dispatch(ReducersIndex.uiReducers.setActiveTab(firstTab));
      dispatch(ReducersIndex.uiReducers.setExitToRoom(true));
      // Pass empty array or null if clearModals requires arguments
      dispatch(ReducersIndex.uiReducers.clearModals([]));
    }
  };

  return (
    <Fade duration={'slow'} show={show}>
      <ThemeProvider theme={muiTheme}>
        <Box 
          className="container-medium"
          sx={{
            backgroundImage: modalContent.image ? `url(${modalContent.image})` : 'none',
            backgroundSize: 'cover',
            zIndex: 1
          }}
        >
          <Box
            className="modal wrappermod"
            tabIndex={-1}
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            sx={{
              background: isTutorial ? 'none' : modalContent.image == null ? `rgba(255, 255, 255, ${hasSpotlight ? 0 : 0.5})` : 'none',
              display: 'block',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              zIndex: 1200
            }}
          >
            <Dialog
              open={show}
              PaperComponent={(modalName === 'requestQuote' || isTutorial) ? undefined : PaperComponent}
              aria-labelledby="draggable-dialog-title"
              maxWidth={isTutorial ? false : 'lg'}
              hideBackdrop={isTutorial}
              disableEnforceFocus={isTutorial}
              disableAutoFocus={isTutorial}
              disableRestoreFocus={isTutorial}
              disableScrollLock={isTutorial}
              disablePortal={false}
              sx={{
                '& .MuiDialog-container': {
                  pointerEvents: isTutorial ? 'none' : 'auto',
                  display: 'flex',
                  alignItems: modalName === 'requestQuote' ? 'center' : 'flex-start',
                  justifyContent: modalName === 'requestQuote' ? 'center' : 'center',
                },
                '& .MuiDialog-paper': {
                  pointerEvents: isTutorial ? 'auto' : 'auto',
                  position: modalName === 'requestQuote' ? 'relative' : (isTutorial ? 'fixed' : 'absolute'),
                  top: modalName === 'requestQuote' ? 'auto' : 
                       (isTutorial ? 
                        (tutorialConfig?.[currentStep]?.isWelcomeScreen ? '50%' : 
                         targetElementTop !== null ? `${targetElementTop}px` : '100px') : 
                        'calc(15% + 30px)'),
                  left: modalName === 'requestQuote' ? 'auto' :
                        (isTutorial ? 
                         (tutorialConfig?.[currentStep]?.isWelcomeScreen ? '50%' : 'calc(320px + 40px)') : 
                         undefined),
                  transform: modalName === 'requestQuote' ? 'none' :
                             (isTutorial ? 
                              (tutorialConfig?.[currentStep]?.isWelcomeScreen ? 'translate(-50%, -50%)' : 'none') : 
                              undefined),
                  margin: modalName === 'requestQuote' ? '0' : 0,
                  backgroundColor: isTutorial ? '#ffffff' : useTheme.background,
                  borderRadius: isTutorial && tutorialConfig[currentStep]?.isWelcomeScreen ? '16px' : 
                                isTutorial ? '8px' : 
                                useTheme.borderRadius || 0,
                  border: isTutorial && tutorialConfig[currentStep]?.isWelcomeScreen ? '2px solid #1863D3' : 'none',
                  opacity: useTheme.opacity,
                  color: isTutorial ? '#2d2f38' : useTheme.color,
                  padding: isTutorial ? 0 : useTheme.padding,
                  width: isTutorial && tutorialConfig[currentStep]?.isWelcomeScreen ? '720px' :
                         isTutorial && tutorialConfig[currentStep]?.isNameDesignScreen ? '480px' :
                         isTutorial ? '600px' : 
                         modalContent.width || undefined,
                  maxHeight: isTutorial ? '80vh' : modalContent.maxHeight || undefined,
                  boxShadow: isTutorial && tutorialConfig[currentStep]?.isWelcomeScreen ? '0 8px 24px rgba(0,0,0,0.15)' :
                            isTutorial ? '0 4px 16px rgba(0,0,0,0.12)' : 
                            useTheme.boxShadow || undefined,
                  zIndex: isTutorial ? 1400 : 1300
                }
              }}
            >
              {/* Header */}
              {isTutorial ? (
                tutorialConfig[currentStep]?.isWelcomeScreen || tutorialConfig[currentStep]?.isNameDesignScreen ? (
                  <MuiDialogTitle
                    sx={{
                      backgroundColor: '#ffffff',
                      color: '#2d2f38',
                      padding: tutorialConfig[currentStep]?.isWelcomeScreen ? '32px 32px 24px' : '24px 32px 16px',
                      borderBottom: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Typography
                      variant={tutorialConfig[currentStep]?.isWelcomeScreen ? "h4" : "h5"}
                      fontWeight={600}
                      sx={{
                        fontSize: tutorialConfig[currentStep]?.isWelcomeScreen ? '28px' : '20px',
                        color: '#111',
                        fontFamily: '"Open Sans", sans-serif'
                      }}
                    >
                      {tutorialConfig[currentStep]?.title}
                    </Typography>
                    <Box
                      component="button"
                      onClick={handleTutorialSkip}
                      sx={{
                        background: 'transparent',
                        border: 'none',
                        color: tutorialConfig[currentStep]?.isNameDesignScreen ? '#1863D3' : '#878787',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: 0,
                        textDecoration: 'underline',
                        fontFamily: '"Open Sans"',
                        fontWeight: 400,
                        '&:hover': {
                          color: tutorialConfig[currentStep]?.isNameDesignScreen ? '#1557B8' : '#646464',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Skip guide
                    </Box>
                  </MuiDialogTitle>
                ) : (
                  <MuiDialogTitle
                    sx={{
                      backgroundColor: 'transparent',
                      color: '#2d2f38',
                      padding: '24px 32px 16px',
                      borderBottom: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h5" fontWeight="600">
                      {tutorialConfig[currentStep]?.title || 'Tutorial'}
                    </Typography>
                    <Box
                      component="button"
                      onClick={handleTutorialSkip}
                      sx={{
                        background: 'transparent',
                        border: 'none',
                        color: '#74777a',
                        cursor: 'pointer',
                        fontSize: '24px',
                        padding: 0,
                        '&:hover': { color: '#2d2f38' }
                      }}
                    >
                      ×
                    </Box>
                  </MuiDialogTitle>
                )
              ) : modalName === 'requestQuote' ? (
                <DialogTitle
                  onClose={() => {
                    // Directly close the modal without going through the close function's checks
                    dispatch(ReducersIndex.uiReducers.closeModal(modalName));
                    dispatch(ReducersIndex.uiReducers.clearModals([]));
                  }}
                >
                  {modalContent.header || "Request Quote"}
                </DialogTitle>
              ) : (
                <MuiDialogTitle
                  className="modalHeader"
                  sx={{
                    backgroundColor: useTheme.headerBackground || useTheme.background,
                    color: useTheme.headerColor || useTheme.color,
                    padding: useTheme.headerPadding,
                    textAlign: useTheme.headerAlign || 'start',
                    borderBottom: useTheme.headerBorder === false ? 'none' : (useTheme.headerBorder ? `1px solid ${useTheme.headerBorder}` : 'none'),
                    cursor: 'move',
                    fontWeight: 'bold'
                  }}
                >
                  {modalContent.header || "Oops..."}
                </MuiDialogTitle>
              )}
              
              {/* Body */}
              {isTutorial && tutorialConfig ? (
                <DialogContent
                  sx={{
                    padding: tutorialConfig[currentStep]?.isWelcomeScreen ? '0 32px 24px' : 
                             tutorialConfig[currentStep]?.isNameDesignScreen ? '0 32px 24px' : '32px',
                    minHeight: tutorialConfig[currentStep]?.isNameDesignScreen ? 'auto' :
                               tutorialConfig[currentStep]?.isWelcomeScreen ? 'auto' : '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: tutorialConfig[currentStep]?.isNameDesignScreen ? 2 :
                         tutorialConfig[currentStep]?.isWelcomeScreen ? 0 : 3,
                    justifyContent: 'flex-start',
                    alignItems: 'stretch',
                    textAlign: 'left',
                    position: 'relative'
                  }}
                >
                  {tutorialConfig[currentStep]?.isWelcomeScreen ? (
                    <>
                      {/* Main content text */}
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '16px',
                          lineHeight: '1.6',
                          color: '#111',
                          fontFamily: '"Open Sans"',
                          mb: 3,
                          px: 0
                        }}
                      >
                        {tutorialConfig[currentStep]?.content}
                      </Typography>
                      
                      {/* Subtitle/Call to action */}
                      {tutorialConfig[currentStep]?.subtitle && (
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: '18px',
                            fontWeight: 600,
                            lineHeight: '1.5',
                            color: '#111',
                            fontFamily: '"Open Sans"',
                            mb: 2
                          }}
                        >
                          {tutorialConfig[currentStep]?.subtitle}
                        </Typography>
                      )}
                    </>
                  ) : tutorialConfig[currentStep]?.isNameDesignScreen ? (
                    <>
                      {/* Description text */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '14px',
                          lineHeight: '1.5',
                          color: '#646464',
                          fontFamily: '"Open Sans"',
                          mb: 3
                        }}
                      >
                        {tutorialConfig[currentStep]?.content}
                      </Typography>
                      
                      {/* Design name input */}
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '14px',
                            color: '#111',
                            fontFamily: '"Open Sans"',
                            fontWeight: 400,
                            mb: 1
                          }}
                        >
                          Design name <span style={{ color: '#646464' }}>(Optional)</span>
                        </Typography>
                        <Box
                          component="input"
                          type="text"
                          placeholder="Name your design"
                          sx={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #E6E7E8',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontFamily: '"Open Sans"',
                            backgroundColor: '#ffffff',
                            outline: 'none',
                            '&:focus': {
                              borderColor: '#1863D3',
                              boxShadow: '0 0 0 2px rgba(24, 99, 211, 0.1)'
                            },
                            '&::placeholder': {
                              color: '#A5AAAD'
                            }
                          }}
                        />
                      </Box>
                      
                      {/* Add Workplace location - expandable section */}
                      <Box sx={{ mb: 2 }}>
                        <Box
                          component="button"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            width: '100%',
                            textAlign: 'left',
                            mb: 2
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 0,
                              height: 0,
                              borderLeft: '5px solid transparent',
                              borderRight: '5px solid transparent',
                              borderTop: '5px solid #646464',
                              transform: 'rotate(-90deg)',
                              transition: 'transform 0.2s'
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '14px',
                              color: '#111',
                              fontFamily: '"Open Sans"',
                              fontWeight: 400
                            }}
                          >
                            Add Workplace location <span style={{ color: '#646464' }}>(Optional)</span>
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Regular tutorial content */}
                      {tutorialConfig[currentStep]?.image && (
                        <Box
                          component="img"
                          src={tutorialConfig[currentStep].image}
                          alt="Tutorial step"
                          sx={{
                            width: '100%',
                            maxHeight: '250px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa',
                            padding: 2
                          }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '16px',
                          lineHeight: '1.6',
                          color: '#2d2f38'
                        }}
                      >
                        {tutorialConfig[currentStep]?.content}
                      </Typography>
                    </>
                  )}
                </DialogContent>
              ) : (
                <DialogContent
                  className="modalBody vstack"
                  sx={{
                    padding: useTheme.bodyPadding,
                    backgroundColor: theme => theme.palette.background.paper
                  }}
                >
                  {/* Support both modalContent.lines and modalContent.body for backward compatibility */}
                  {(modalContent.lines || modalContent.body) ? 
                    (modalContent.lines || modalContent.body).map((line: any, index: number) => {
                      if ((!hasCamera && line.label === 'Model') || 
                          (hasVideoBar && line.label === 'Model')) {
                        return null;
                      }
                      
                      const lineProps = {
                        ...line,
                        dispatcher: line.dispatcher || modalContent.dispatcher,
                      };
                      
                      return <Line key={`${line.name}-${index}`} {...lineProps} show={show} />;
                    }) : (
                      <Box>This modal has not been created yet</Box>
                    )
                  }
                </DialogContent>
              )}
              
              {/* Footer */}
              {isTutorial ? (
                <DialogActions
                  sx={{
                    backgroundColor: '#ffffff',
                    borderTop: 'none',
                    padding: tutorialConfig[currentStep]?.isWelcomeScreen ? '24px 32px 32px' :
                            tutorialConfig[currentStep]?.isNameDesignScreen ? '0 32px 24px' : '16px 32px',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 0
                  }}
                >
                  {tutorialConfig[currentStep]?.isWelcomeScreen ? (
                    <>
                      {/* Button row */}
                      <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                        <Box
                          component="button"
                          onClick={handleTutorialPrev}
                          disabled={currentStep === 0}
                          sx={{
                            backgroundColor: '#ffffff',
                            color: currentStep === 0 ? '#BDBDBD' : '#111',
                            border: '1px solid',
                            borderColor: currentStep === 0 ? '#E6E7E8' : '#D0D1D2',
                            borderRadius: '6px',
                            padding: '12px 32px',
                            fontSize: '16px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 500,
                            cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                            minWidth: '120px',
                            '&:hover': {
                              backgroundColor: currentStep === 0 ? '#ffffff' : '#F8F9FA',
                              borderColor: currentStep === 0 ? '#E6E7E8' : '#BDBDBD'
                            }
                          }}
                        >
                          Back
                        </Box>
                        <Box
                          component="button"
                          onClick={handleTutorialNext}
                          sx={{
                            backgroundColor: '#1863D3',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '12px 32px',
                            fontSize: '16px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 500,
                            cursor: 'pointer',
                            minWidth: '200px',
                            '&:hover': {
                              backgroundColor: '#1557B8'
                            }
                          }}
                        >
                          {tutorialConfig[currentStep]?.buttonText || 'Create design'}
                        </Box>
                      </Box>
                      
                      {/* Progress dots */}
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'space-between', width: '100%', mt: 2 }}>
                        {Array.from({ length: tutorialConfig?.length || 14 }, (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              flex: i === currentStep ? 3 : 1,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: i === currentStep ? '#1863D3' : 
                                              i < currentStep ? '#8FB3E1' : '#D9E2EC',
                              transition: 'all 0.3s ease'
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  ) : tutorialConfig[currentStep]?.isNameDesignScreen ? (
                    <>
                      {/* Button row */}
                      <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                        <Box
                          component="button"
                          onClick={handleTutorialPrev}
                          disabled={currentStep === 0}
                          sx={{
                            backgroundColor: '#ffffff',
                            color: currentStep === 0 ? '#A5AAAD' : '#111',
                            border: '1px solid #E6E7E8',
                            borderRadius: '6px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 500,
                            cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                            opacity: currentStep === 0 ? 0.5 : 1,
                            minWidth: '80px',
                            '&:hover': {
                              backgroundColor: currentStep === 0 ? '#ffffff' : '#F8F9FA',
                              borderColor: currentStep === 0 ? '#E6E7E8' : '#D0D1D2'
                            }
                          }}
                        >
                          Back
                        </Box>
                        <Box
                          component="button"
                          onClick={handleTutorialNext}
                          sx={{
                            backgroundColor: '#1863D3',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '10px 24px',
                            flex: 1,
                            fontSize: '14px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#1557B8'
                            }
                          }}
                        >
                          {tutorialConfig[currentStep]?.buttonText || 'Next'}
                        </Box>
                      </Box>
                      
                      {/* Progress dots */}
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'space-between', mt: 2, width: '100%' }}>
                        {Array.from({ length: tutorialConfig?.length || 0 }, (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              flex: i === currentStep ? 3 : 1,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: i === currentStep ? '#1863D3' : 
                                              i < currentStep ? '#8FB3E1' : '#E6E7E8',
                              transition: 'all 0.3s ease'
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  ) : tutorialConfig[currentStep]?.isWelcomeScreen ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        component="button"
                        onClick={handleTutorialSkip}
                        sx={{
                          backgroundColor: '#ffffff',
                          color: '#2d2f38',
                          border: '1px solid #e6e7e8',
                          borderRadius: '8px',
                          padding: '12px 32px',
                          fontSize: '16px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          }
                        }}
                      >
                        Skip for now
                      </Box>
                      <Box
                        component="button"
                        onClick={handleTutorialNext}
                        sx={{
                          backgroundColor: '#1863D3',
                          background: 'linear-gradient(135deg, #1863D3 0%, #1557B8 100%)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '12px 32px',
                          fontSize: '16px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1557B8 0%, #1863D3 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(24, 99, 211, 0.3)'
                          }
                        }}
                      >
                        Get Started
                        <Box component="span" sx={{ fontSize: '20px' }}>→</Box>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      {/* Regular tutorial steps - buttons */}
                      <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                        <Box
                          component="button"
                          onClick={handleTutorialPrev}
                          disabled={currentStep === 0}
                          sx={{
                            backgroundColor: '#ffffff',
                            color: currentStep === 0 ? '#A5AAAD' : '#111',
                            border: '1px solid #E6E7E8',
                            borderRadius: '6px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 500,
                            cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                            opacity: currentStep === 0 ? 0.5 : 1,
                            minWidth: '80px',
                            '&:hover': {
                              backgroundColor: currentStep === 0 ? '#ffffff' : '#F8F9FA',
                              borderColor: currentStep === 0 ? '#E6E7E8' : '#D0D1D2'
                            }
                          }}
                        >
                          Back
                        </Box>
                        <Box
                          component="button"
                          onClick={
                            currentStep === (tutorialConfig?.length || 0) - 1
                              ? handleTutorialSkip
                              : handleTutorialNext
                          }
                          sx={{
                            backgroundColor: '#1863D3',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '10px 24px',
                            flex: 1,
                            fontSize: '14px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#1557B8'
                            }
                          }}
                        >
                          {currentStep === (tutorialConfig?.length || 0) - 1 ? 'Finish guide' : 'Next'}
                        </Box>
                      </Box>
                      
                      {/* Progress dots for regular tutorial steps */}
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'space-between', width: '100%', mt: 2 }}>
                        {Array.from({ length: tutorialConfig?.length || 0 }, (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              flex: i === currentStep ? 3 : 1,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: i === currentStep ? '#1863D3' : 
                                              i < currentStep ? '#8FB3E1' : '#E6E7E8',
                              transition: 'all 0.3s ease'
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </DialogActions>
              ) : (
                <DialogActions
                  className={modalName === 'requestQuote' ? 'modal-footer text-center h-stack d-flex' : `modal-footer mt-4 py-2 text-center h-stack d-flex`}
                  sx={{
                    backgroundColor: theme => theme.palette.background.paper  ,
                    marginTop: modalName === 'requestQuote' ? '0px' : useTheme.footerMargin,
                    padding: modalName === 'requestQuote' ? '0px 24px' : (useTheme.footerPadding || useTheme.bodyPadding),
                    paddingBottom: modalName === 'requestQuote' ? '30px' : undefined,
                    marginBottom: modalName === 'requestQuote' ? '0' : undefined,
                    borderTop: useTheme.footerBorder ? `1px solid ${useTheme.footerBorder}` : 'none',
                    color: 'var(--app-red)',
                    height: 'auto',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: modalName === 'requestQuote' ? 'space-between' :
                      ((modalContent.buttons?.length <= 1 ||
                      modalContent.buttons?.some((btn: any) => btn.label === 'CANCEL' || btn.label === 'Cancel')) ?
                      'center' : 'space-between'),
                    gap: modalName === 'requestQuote' ? 2 : undefined
                  }}
                >
                  <ModalFooterButtons
                    buttons={modalContent.buttons || modalContent.footer || [{ label: 'Close' }]}
                    hideNext={hideNext}
                    modalReopen={modalReopen}
                    closeModal={close}
                    modalName={modalName}
                  />
                </DialogActions>
              )}
            </Dialog>
          </Box>
        </Box>
      </ThemeProvider>
    </Fade>
  );
};

export default MuiModal;
